import { apiSlice } from "../apiSlice";

export interface CreateFeatureGroupRequest {
    displayName: string;
    description: string;
}

export interface CreateFeatureGroupResponse {
    displayName: string;
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    }
}

export interface UpdateFeatureGroupRequest {
    id: string;
    displayName: string;
    description: string;
}

export interface FeatureGroupItem {
    id: string;
    displayName?: string; 
    name?: string; 
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt?: {
        _seconds: number;
        _nanoseconds: number;
    };
}

export interface GetAllFeatureGroupsResponse {
    featureGroups: FeatureGroupItem[];
}

export interface FeatureItem {
    id: string;
    featureGroupId?: string; 
    displayName: string;
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt?: {
        _seconds: number;
        _nanoseconds: number;
    };
    isActive?: boolean; 
}

export interface CreateFeatureRequest {
    featureGroupId: string;
    displayName: string;
    description: string;
}

export interface CreateFeatureResponse {
    id: string;
    featureGroupId: string;
    displayName: string;
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    isActive: boolean;
}

export interface GetAllFeaturesResponse extends Array<FeatureItem> { }

export interface UpdateFeatureRequest {
    id: string;
    featureGroupId: string;
    displayName: string;
    description: string;
}

export interface UpdateFeatureResponse {
    featureGroupId: string;
    displayName: string;
    description: string;
    updatedAt: {
        _seconds: number;
        _nanoseconds: number;
    };
}

// Role interfaces
export interface RoleItem {
    id: string;
    displayName: string;
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt?: {
        _seconds: number;
        _nanoseconds: number;
    };
    isActive?: boolean;
    roleName: string;
}

export interface CreateRoleRequest {
    displayName: string;
    description: string;
}

export interface CreateRoleResponse {
    displayName: string;
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    isActive: boolean;
    roleName: string;
}

export interface UpdateRoleRequest {
    id: string;
    displayName: string;
    description: string;
}

export interface UpdateRoleResponse {
    displayName: string;
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    }
    isActive: boolean;
    roleName: string;
    updatedAt: {
        _seconds: number;
        _nanoseconds: number;
    };
}

export interface PermissionMatrixRoleItem {
    id: string;
    displayName: string;
    description: string;
    roleName: string;
    isActive: boolean;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt?: {
        _seconds: number;
        _nanoseconds: number;
    };
    createdBy?: string;
    permissions?: {
        [featureId: string]: {
            list: boolean;
            view: boolean;
            edit: boolean;
            delete: boolean;
        };
    };
}

interface GetAllPermissionMatrixDataResponse extends Array<PermissionMatrixRoleItem> {}

export interface GetAllRolesResponse extends Array<RoleItem> { }

const API_BASE_DOMAIN = process.env.REACT_APP_API_BASE_DOMAIN_DEV || process.env.REACT_APP_API_BASE_DOMAIN_PRODUCTION;
export const rolesPermissionService = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        createFeatureGroup: builder.mutation<CreateFeatureGroupResponse, CreateFeatureGroupRequest>({
            query: (requestData) => ({
                url: `https://superadminhttps-createfeaturegroup-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "POST",
                body: requestData,
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["FeatureGroups", "PermissionMatrix"],
        }),
        updateFeatureGroup: builder.mutation<CreateFeatureGroupResponse, UpdateFeatureGroupRequest>({
            query: (requestData) => ({
                url: `https://superadminhttps-updatefeaturegroup-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "PUT",
                body: requestData,
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["FeatureGroups", "PermissionMatrix"],
        }),
        deleteFeatureGroup: builder.mutation<void, { featureGroupId: string }>({
            query: (requestData) => ({
                url: `https://superadminhttps-deletefeaturegroup-${API_BASE_DOMAIN}-uc.a.run.app/?featureGroupId=${requestData.featureGroupId}`,
                method: "DELETE",
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["FeatureGroups", "PermissionMatrix"],
        }),
        getFeatureGroups: builder.query<GetAllFeatureGroupsResponse, void>({
            query: () => ({
                url: `https://superadminhttps-getallfeaturegroup-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "GET",
                responseHandler: (response) => response.json(),
            }),
            providesTags: ["FeatureGroups"],
        }),
        createFeature: builder.mutation<CreateFeatureResponse, CreateFeatureRequest>({
            query: (requestData) => ({
                url: `https://superadminhttps-createfeature-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "POST",
                body: requestData,
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["Features", "PermissionMatrix"],
        }),
        deleteFeature: builder.mutation<void, { featureId: string }>({
            query: (requestData) => ({
                url: `https://superadminhttps-deletefeature-${API_BASE_DOMAIN}-uc.a.run.app/?featureId=${requestData.featureId}`,
                method: "DELETE",
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["Features", "PermissionMatrix"],
        }),
        getAllFeatures: builder.query<GetAllFeaturesResponse, void>({
            query: () => ({
                url: `https://superadminhttps-getallfeature-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "GET",
                responseHandler: (response) => response.json(),
            }),
            providesTags: ["Features"],
        }),
        updateFeature: builder.mutation<UpdateFeatureResponse, UpdateFeatureRequest>({
            query: (requestData) => ({
                url: `https://superadminhttps-updatefeature-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "PUT",
                body: requestData,
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["Features", "PermissionMatrix"],
        }),
        createRole: builder.mutation<CreateRoleResponse, CreateRoleRequest>({
            query: (requestData) => ({
                url: `https://superadminhttps-createrole-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "POST",
                body: requestData,
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["Roles", "PermissionMatrix"],
        }),
        updateRole: builder.mutation<UpdateRoleResponse, UpdateRoleRequest>({
            query: (requestData) => ({
                url: `https://superadminhttps-updaterole-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "PUT",
                body: requestData,
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["Roles", "PermissionMatrix"],
        }),
        deleteRole: builder.mutation<void, { roleId: string }>({
            query: (requestData) => ({
                url: `https://superadminhttps-deleterole-${API_BASE_DOMAIN}-uc.a.run.app/?roleId=${requestData.roleId}`,
                method: "DELETE",
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["Roles", "PermissionMatrix"],
        }),
        getAllRoles: builder.query<GetAllRolesResponse, void>({
            query: () => ({
                url: `https://superadminhttps-getallroles-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "GET",
                responseHandler: (response) => response.json(),
            }),
            providesTags: ["Roles"],
        }),
        getAllPermissionMatrixData: builder.query<GetAllPermissionMatrixDataResponse, void>({
            query: () => ({
                url: `https://superadminhttps-getallroles-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "GET",
                responseHandler: (response) => response.json(),
            }),
            providesTags: ["PermissionMatrix"],
        }),
        updatePermissions: builder.mutation<void, { 
          roles: {
            id: string;
            permissions: {
              [featureKey: string]: {
                [actionName: string]: boolean;
              };
            };
          }[];
        }>({
            query: (requestData) => ({
                url: `https://superadminhttps-updatepermissionmatrix-${API_BASE_DOMAIN}-uc.a.run.app/`,
                method: "POST",
                body: requestData,
                responseHandler: (response) => response.text(),
            }),
            invalidatesTags: ["PermissionMatrix"],
        }),
    }),
});

export const {
    useCreateFeatureGroupMutation,
    useUpdateFeatureGroupMutation,
    useDeleteFeatureGroupMutation,
    useGetFeatureGroupsQuery,
    useLazyGetFeatureGroupsQuery,
    useCreateFeatureMutation,
    useDeleteFeatureMutation,
    useGetAllFeaturesQuery,
    useLazyGetAllFeaturesQuery,
    useUpdateFeatureMutation,
    useCreateRoleMutation,
    useUpdateRoleMutation,
    useDeleteRoleMutation,
    useGetAllRolesQuery,
    useLazyGetAllRolesQuery,
    useGetAllPermissionMatrixDataQuery,
    useLazyGetAllPermissionMatrixDataQuery,
    useUpdatePermissionsMutation,
} = rolesPermissionService;